import { useEffect, useRef } from 'react'
import { useReducedMotion } from 'framer-motion'
import { stepWorld } from '../engine/physics'
import type { BraitenbergWorld, VehicleConfig } from '../engine/types'
import { vehicle1 } from '../engine/vehicles'

const HERO_BOUNDS = { width: 300, height: 200 }
const HERO_SOURCE = {
  id: 'hero-light',
  position: { x: 82, y: 132 },
  intensity: 900,
}
const HERO_START = { x: 258, y: 76 }
const HERO_HEADING = 2.72
const HERO_TRAIL_LENGTH = 240
const HERO_FIXED_STEP = 1 / 30
const HERO_TIME_SCALE = 0.72

function seededRandom(seed: number): () => number {
  let value = seed >>> 0
  return () => {
    value += 0x6d2b79f5
    let result = value
    result = Math.imul(result ^ (result >>> 15), result | 1)
    result ^= result + Math.imul(result ^ (result >>> 7), result | 61)
    return ((result ^ (result >>> 14)) >>> 0) / 4294967296
  }
}

function createHeroState(): {
  world: BraitenbergWorld
  config: VehicleConfig
} {
  const preset = vehicle1(HERO_START, HERO_HEADING)
  const config = {
    ...preset.config,
    // The same Vehicle 1 mechanism, with a quieter editorial noise setting for the opening.
    brownianNoise: 1.8,
  }
  const state = {
    ...preset.initialState,
    position: { ...preset.initialState.position },
    trail: [
      { x: 288, y: 62 },
      { x: 279, y: 65 },
      { x: 270, y: 69 },
      { x: HERO_START.x, y: HERO_START.y },
    ],
  }

  return {
    config,
    world: {
      vehicles: [state],
      sources: [{ ...HERO_SOURCE, position: { ...HERO_SOURCE.position } }],
      bounds: HERO_BOUNDS,
      trailLength: HERO_TRAIL_LENGTH,
    },
  }
}

function trailPath(points: readonly { x: number; y: number }[]): string {
  if (points.length < 2) return ''

  let path = `M${points[0]!.x.toFixed(2)} ${points[0]!.y.toFixed(2)}`
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1]!
    const point = points[index]!
    if (
      Math.abs(point.x - previous.x) > HERO_BOUNDS.width * 0.5
      || Math.abs(point.y - previous.y) > HERO_BOUNDS.height * 0.5
    ) {
      path += ` M${point.x.toFixed(2)} ${point.y.toFixed(2)}`
    } else {
      path += ` L${point.x.toFixed(2)} ${point.y.toFixed(2)}`
    }
  }
  return path
}

export function HeroVehicle() {
  const reducedMotion = useReducedMotion() ?? false
  const svgRef = useRef<SVGSVGElement>(null)
  const vehicleRef = useRef<SVGGElement>(null)
  const oldTrailRef = useRef<SVGPathElement>(null)
  const middleTrailRef = useRef<SVGPathElement>(null)
  const recentTrailRef = useRef<SVGPathElement>(null)

  useEffect(() => {
    const svg = svgRef.current
    const vehicleElement = vehicleRef.current
    if (!svg || !vehicleElement) return

    const { world, config } = createHeroState()
    const rng = seededRandom(0x1984b7)
    let animationFrame: number | null = null
    let lastTimestamp: number | null = null
    let carry = 0
    let inView = true

    const render = () => {
      const vehicle = world.vehicles[0]!
      const degrees = (vehicle.heading * 180) / Math.PI
      vehicleElement.setAttribute(
        'transform',
        `translate(${vehicle.position.x.toFixed(2)} ${vehicle.position.y.toFixed(2)}) rotate(${degrees.toFixed(2)}) scale(1.16)`,
      )

      const trail = vehicle.trail
      const firstCut = Math.floor(trail.length * 0.38)
      const secondCut = Math.floor(trail.length * 0.7)
      oldTrailRef.current?.setAttribute('d', trailPath(trail.slice(0, firstCut + 1)))
      middleTrailRef.current?.setAttribute(
        'd',
        trailPath(trail.slice(Math.max(0, firstCut - 1), secondCut + 1)),
      )
      recentTrailRef.current?.setAttribute(
        'd',
        trailPath(trail.slice(Math.max(0, secondCut - 1))),
      )
    }

    render()
    if (reducedMotion) return

    const tick = (timestamp: number) => {
      if (!inView || document.hidden) {
        lastTimestamp = timestamp
        animationFrame = requestAnimationFrame(tick)
        return
      }

      if (lastTimestamp !== null) {
        carry += Math.min((timestamp - lastTimestamp) / 1000, 0.05) * HERO_TIME_SCALE
        while (carry >= HERO_FIXED_STEP) {
          stepWorld(world, [config], HERO_FIXED_STEP, rng)
          carry -= HERO_FIXED_STEP
        }
        render()
      }
      lastTimestamp = timestamp
      animationFrame = requestAnimationFrame(tick)
    }

    const observer =
      typeof IntersectionObserver === 'undefined'
        ? null
        : new IntersectionObserver(([entry]) => {
            inView = entry?.isIntersecting ?? true
          }, { rootMargin: '120px 0px' })
    observer?.observe(svg)
    animationFrame = requestAnimationFrame(tick)

    return () => {
      observer?.disconnect()
      if (animationFrame !== null) cancelAnimationFrame(animationFrame)
    }
  }, [reducedMotion])

  return (
    <svg
      ref={svgRef}
      className="bv-hero-vehicle"
      viewBox="0 0 300 200"
      role="img"
      aria-labelledby="bv-hero-vehicle-title bv-hero-vehicle-desc"
      data-motion-model="vehicle-1-brownian"
      data-start-side="right"
    >
      <title id="bv-hero-vehicle-title">Vehicle 1 wandering through a light field</title>
      <desc id="bv-hero-vehicle-desc">
        A one-sensor, one-motor Braitenberg vehicle begins on the right and wanders slowly across
        the field. The light changes its speed while Brownian heading noise changes its direction.
      </desc>

      <g className="bv-hero-source" transform={`translate(${HERO_SOURCE.position.x} ${HERO_SOURCE.position.y})`}>
        <circle r="4.5" />
        <circle className="bv-hero-source-ring" r="14" />
        <circle className="bv-hero-source-field" r="33" />
        <path d="M0 -22 V-28 M0 22 V28 M-22 0 H-28 M22 0 H28" />
      </g>

      <path ref={oldTrailRef} className="bv-hero-trail bv-hero-trail-old" />
      <path ref={middleTrailRef} className="bv-hero-trail bv-hero-trail-middle" />
      <path ref={recentTrailRef} className="bv-hero-trail bv-hero-trail-recent" />

      <g
        ref={vehicleRef}
        className="bv-hero-machine"
        transform={`translate(${HERO_START.x} ${HERO_START.y}) rotate(${(HERO_HEADING * 180) / Math.PI}) scale(1.16)`}
      >
        <path className="bv-hero-body" d="M-15 -10 C-5 -13 7 -12 15 -7 L17 7 C7 12 -6 13 -15 9Z" />
        <rect className="bv-hero-wheel" x="-8" y="-15" width="13" height="4" rx="1" />
        <rect className="bv-hero-wheel" x="-8" y="11" width="13" height="4" rx="1" />
        <path d="M15 0 C20 0 23 0 27 0" />
        <circle className="bv-hero-sensor" cx="30" cy="0" r="3.8" />
        <path className="bv-hero-wire" d="M29 0 C13 -1 8 7 -2 12" />
      </g>
    </svg>
  )
}
